import { Box, BoxProps, Collapse, IconButton, Typography } from "@mui/material";
import React, { ReactNode, useState } from "react";
import { CHART_COLORS, CHART_DIMENSIONS, CHART_TYPOGRAPHY } from "./tokens";
import { AddIcon, ExpandMoreIcon } from "../../../../svg-icons/material-icons";

interface AccordionRecordProps {
  title: string;
  children?: ReactNode;
  expanded?: boolean;
  onToggle?: () => void;
  onAdd?: () => void;
  hideAddButton?: boolean;
  rightActions?: ReactNode;
  showSeparator?: boolean;
  sx?: BoxProps["sx"];
}

const AccordionRecord: React.FC<AccordionRecordProps> = ({
  title,
  children,
  expanded: controlledExpanded,
  onToggle,
  onAdd,
  hideAddButton = false,
  rightActions,
  showSeparator = true,
  sx,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);

  const isControlled = controlledExpanded !== undefined;
  const isExpanded = isControlled ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else if (!isControlled) {
      setInternalExpanded((prev) => !prev);
    }
  };

  return (
    <Box
      sx={{
        borderBottom: showSeparator
          ? `1px solid ${CHART_COLORS.border}`
          : "none",
        width: "100%",
        paddingX: "12px",
        bgcolor: CHART_COLORS.surfaceDefault,
        "&:hover": {
          bgcolor: CHART_COLORS.secondaryHover,
          borderBottom: `1px solid ${CHART_COLORS.primary}`,
        },
        ...sx,
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        onClick={handleToggle}
        sx={{
          cursor: "pointer",
          height: CHART_DIMENSIONS.accordionHeaderHeight,
          // px: "6px",
          userSelect: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            color: CHART_COLORS.primary,
            transition: "transform 0.2s ease",
          }}
        >
          <ExpandMoreIcon
            sx={{
              transition: "transform 0.2s ease",
              transform: isExpanded ? "rotate(-180deg)" : "rotate(-360deg)",
            }}
          />
        </Box>

        <Typography
          sx={{
            flex: 1,
            ml: "8px",
            ...CHART_TYPOGRAPHY.body5,
            fontWeight: CHART_TYPOGRAPHY.weight.regular,
            color: CHART_COLORS.textDark,
            fontFamily: CHART_TYPOGRAPHY.fontFamily,
          }}
        >
          {title}
        </Typography>

        {rightActions && (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{ display: "flex", alignItems: "center", gap: "2px" }}
          >
            {rightActions}
          </Box>
        )}

        {!hideAddButton && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onAdd?.();
            }}
            sx={{
              p: "2px",
              color: CHART_COLORS.primary,
              flexShrink: 0,
            }}
            aria-label={`Add to ${title}`}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>

      <Collapse in={isExpanded} unmountOnExit>
        <Box
          sx={{
            borderTop: `1px solid ${CHART_COLORS.border}`,
            bgcolor: CHART_COLORS.surfaceWhite,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AccordionRecord;
